import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../services/firebase";
import { authService } from "../auth/authService";
import { useAutoLogout } from "../hooks/useAutoLogout";
// TODO(api): Replace direct kycService and usersService imports with API calls
// import { kycService } from '../services/kycService';
// import { usersService } from '../users/usersService';
import { activityService } from "../activity/activityService";
// TODO(api): Replace direct auditService import with API call
// import { auditService } from '../services/auditService';
// import { auditService } from "../services/auditService";
import { collection, addDoc } from "firebase/firestore";

// Fallback functions for user profile and tier calculation
const getUserProfile = async (userId) => {
    // TODO(api): Replace with actual API call
    try {
    // Import Firestore functions dynamically to avoid circular dependencies
        const { doc, getDoc } = await import("firebase/firestore");
        const { db } = await import("../services/firebase");
    
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
                ...userData,
                tier: userData.tier || "Newcomer",
                badges: userData.badges || [],
                totalInvestment: userData.totalInvestment || 0,
                totalInvested: userData.totalInvested || 0,
                investmentCount: userData.investmentCount || 0,
                balance: userData.balance || 0,
                role: userData.role || "user", // This is the key field for admin permissions
            };
        } else {
            return {
                tier: "Newcomer",
                badges: [],
                totalInvestment: 0,
                totalInvested: 0,
                investmentCount: 0,
                balance: 0,
                role: "user",
            };
        }
    } catch (error) {
        // console.error("Error fetching user profile from Firestore:", error);
        return {
            tier: "Newcomer",
            badges: [],
            totalInvestment: 0,
            totalInvested: 0,
            investmentCount: 0,
            balance: 0,
            role: "user",
        };
    }
};

const calculateUserTier = (totalInvestment) => {
    // TODO(api): Replace with actual tier calculation logic
    if (totalInvestment >= 50000) return { name: "Diamond" };
    if (totalInvestment >= 25000) return { name: "Platinum" };
    if (totalInvestment >= 10000) return { name: "Gold" };
    if (totalInvestment >= 5000) return { name: "Silver" };
    if (totalInvestment >= 1000) return { name: "Bronze" };
    return { name: "Newcomer" };
};

const logKYCAudit = async ({ userId, changedBy, adminId, oldStatus, newStatus, reason }) => {
    const { db } = await import("../services/firebase");
    await addDoc(collection(db, "kycAuditLogs"), {
        userId,
        changedBy,
        adminId: adminId || changedBy, // Use adminId if provided, fallback to changedBy
        oldStatus,
        newStatus,
        reason: reason || null,
        timestamp: new Date().toISOString(),
    });
};

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [kycData, setKycData] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(true);

    // Configurar auto logout
    useAutoLogout({
        currentUser,
        logout,
        inactivityTimeout: 30 * 60 * 1000, // 30 minutos
        enableInactivityLogout: true,
        enablePageUnloadLogout: true,
        enableOfflineLogout: true
    });

    // Load user profile and KYC data
    const loadUserData = useCallback(async (user) => {
        try {
            // Ensure user profile exists
            await authService.ensureUserProfile(user);

            // Get user profile from Firestore
            const userProfile = await getUserProfile(user.uid);
      
            // Calculate user tier based on total investment
            const calculatedTier = calculateUserTier(userProfile.totalInvestment || 0);
      
            // Update user tier based on total investment
            // TODO(api): Replace with actual API call when backend is ready
            if (calculatedTier && calculatedTier.name !== userProfile.tier) {
                // console.log('Would update user tier to:', calculatedTier.name);
            }

            // Ensure gamification fields exist with default values
            const updatedProfile = {
                ...userProfile,
                tier: userProfile?.tier || "Newcomer",
                badges: userProfile?.badges || [],
                totalInvested: userProfile?.totalInvested || 0,
                investmentCount: userProfile?.investmentCount || 0,
            };
      
            // Update profile if defaults were added
            // TODO(api): Replace with actual API call when backend is ready
            if (!userProfile?.tier || !userProfile?.badges) {
                // console.log('Would update profile with defaults:', {
                //   tier: updatedProfile.tier,
                //   badges: updatedProfile.badges,
                //   totalInvested: updatedProfile.totalInvested,
                //   investmentCount: updatedProfile.investmentCount
                // });
            }
      
            setUserProfile(updatedProfile);
            setKycData({ status: updatedProfile.kycStatus });
      
            // Load KYC data
            // TODO(api): Replace with actual API call when backend is ready
            // const kycStatus = await fetch('/api/kyc/status/' + user.uid);
            // setKycData(kycStatus.data);
      
            // Update user profile with KYC status if not already set
            // TODO(api): Replace with actual API call when backend is ready
            if (updatedProfile && !updatedProfile.kycStatus) {
                // console.log('Would update KYC status in profile');
            }
        } catch (error) {
            await handleFirestoreError(error, user, logout);
            // console.error("Error loading user data:", error);
            setUserProfile(null);
            setKycData(null);
        }
    }, []);

    const refreshUserData = async () => {
        if (currentUser) {
            setLoading(true);
            await loadUserData(currentUser);
            setLoading(false);
        }
    };

    async function signup(email, password) {
        const cred = await authService.signup(email, password);
        setCurrentUser(cred.user);
        await loadUserData(cred.user);
        return cred;
    }

    async function login(email, password) {
        const cred = await authService.login(email, password);
        setCurrentUser(cred.user);
        await loadUserData(cred.user);
        return cred;
    }

    async function loginWithGoogle() {
        const cred = await authService.loginWithGoogle();
        setCurrentUser(cred.user);
        await loadUserData(cred.user);
        return cred;
    }

    async function loginWithMicrosoft() {
        const cred = await authService.loginWithMicrosoft();
        setCurrentUser(cred.user);
        await loadUserData(cred.user);
        return cred;
    }

    function logout() {
        setKycData(null);
        return authService.logout();
    }

    function resetPassword(email) {
        return authService.resetPassword(email);
    }

    async function updateBalance(amount) {
        if (!currentUser) return;
        // TODO(api): Replace with actual API call when backend is ready
        // console.log("Would update balance:", { currentBalance: userProfile?.balance, amount });
    
        // Only create a transaction for actual deposits (i.e., positive amounts).
        // Investment-related transactions are created separately in the InvestmentForm.
        if (amount > 0) {
            await activityService.createTransaction({
                userId: currentUser.uid,
                type: "deposit",
                amount: amount,
                description: "User deposited funds"
            });
        }
    }

    // Update KYC data when it changes
    const updateKYCData = async (kycStatus, reason, isAdminAction = false) => {
        if (currentUser && kycStatus) {
            const { doc, updateDoc, getDoc } = await import("firebase/firestore");
            const { db } = await import("../services/firebase");
            const userRef = doc(db, "users", currentUser.uid);
            const userSnap = await getDoc(userRef);
            const oldStatus = userSnap.exists() ? userSnap.data().kycStatus : null;
            await updateDoc(userRef, {
                kycStatus,
                kycSubmittedAt: new Date().toISOString(),
            });
            await logKYCAudit({
                userId: currentUser.uid,
                changedBy: currentUser.email,
                adminId: isAdminAction ? currentUser.uid : null, // Include adminId for admin actions
                oldStatus,
                newStatus: kycStatus,
                reason,
            });
            await loadUserData(currentUser);
        } else if (currentUser) {
            // Just refresh user data, do not overwrite kycStatus!
            await loadUserData(currentUser);
        }
    };

    // Admin function to update KYC status for any user
    const updateUserKYCStatus = async (targetUserId, kycStatus, reason) => {
        if (!currentUser) return;
        
        const { doc, updateDoc, getDoc } = await import("firebase/firestore");
        const { db } = await import("../services/firebase");
        const { adminAuditService } = await import("../services/adminAuditService");
        
        // Get the target user's current KYC status
        const userRef = doc(db, "users", targetUserId);
        const userSnap = await getDoc(userRef);
        const oldStatus = userSnap.exists() ? userSnap.data().kycStatus : null;
        
        // Update the target user's KYC status
        await updateDoc(userRef, {
            kycStatus,
            kycUpdatedAt: new Date().toISOString(),
        });
        
        // Log the admin action with comprehensive details
        await adminAuditService.logKYCAdminAction(currentUser.uid, "kyc_status_update", targetUserId, {
            adminEmail: currentUser.email,
            oldValue: oldStatus,
            newValue: kycStatus,
            reason: reason,
            ipAddress: "frontend", // Would be captured from request in real implementation
            userAgent: navigator.userAgent,
            sessionId: "frontend_session"
        });
        
        // Also log to the existing KYC audit log for backward compatibility
        await logKYCAudit({
            userId: targetUserId,
            changedBy: currentUser.email,
            adminId: currentUser.uid,
            oldStatus,
            newStatus: kycStatus,
            reason,
        });
    };

    /**
     * Cambia el contexto activo del usuario (empresa/particular/etc.)
     * @param {object} newContext - Objeto de contexto seleccionado
     */
    const switchContext = async (newContext) => {
        if (!currentUser) return;
        try {
            // Actualiza el contexto activo en el backend/perfil
            const { doc, updateDoc } = await import("firebase/firestore");
            const { db } = await import("../services/firebase");
            const userRef = doc(db, "users", currentUser.uid);
            await updateDoc(userRef, { activeContext: newContext.name });
            // Refresca el perfil tras el cambio
            await refreshUserData();
        } catch (error) {
            // Manejo profesional de errores: no hacer logout, solo feedback
            // eslint-disable-next-line no-console
            console.error("Error cambiando de contexto:", error);
        }
    };

    useEffect(() => {
        // console.log("🔍 AuthContext: Configurando listener de autenticación...");
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            // console.log("🔍 AuthContext: Cambio de estado de autenticación:", user ? "Usuario detectado" : "Sin usuario");
            setCurrentUser(user);
            if (user) {
                // console.log("🔍 AuthContext: Cargando datos del usuario:", user.email);
                await loadUserData(user);
                // console.log("🔍 AuthContext: Datos del usuario cargados");
            } else {
                // console.log("🔍 AuthContext: Limpiando datos de usuario");
                setUserProfile(null);
                setKycData(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, [loadUserData]);

    useEffect(() => {
        if (currentUser) {
            // TODO(api): Replace // TODO(api): Replace auditService usage with API call
            // auditService.* usages with API call
            // // TODO(api): Replace auditService usage with API call
            // auditService.logUserAction(currentUser.uid, 'login', { category: 'auth' });
        }
        return () => {
            if (currentUser) {
                // TODO(api): Replace // TODO(api): Replace auditService usage with API call
                // auditService.* usages with API call
                // // TODO(api): Replace auditService usage with API call
                // auditService.logUserAction(currentUser.uid, 'logout', { category: 'auth' });
            }
        };
    }, [currentUser]);

    const value = {
        currentUser,
        userProfile,
        kycData,
        role: userProfile?.role || "user",
        isAdmin: userProfile?.role?.toUpperCase() === "ADMIN" || userProfile?.isAdmin === true,
        isOffboardingPending: userProfile?.offboardingStatus === "pending",
        signup,
        login,
        logout,
        resetPassword,
        loginWithGoogle,
        loginWithMicrosoft,
        updateBalance,
        updateKYCData,
        updateUserKYCStatus,
        refreshUserData,
        switchContext
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

async function handleFirestoreError(error, currentUser, logout) {
    if (
        error.code === "permission-denied" ||
    error.message?.includes("insufficient permissions")
    ) {
        // await auditService.logComplianceEvent("offboarded_access_attempt", {
        //     userId: currentUser?.uid,
        //     email: currentUser?.email,
        //     path: window.location.pathname,
        //     message: error.message,
        // });
        alert(
            "Your account is being offboarded. Please contact support if you need assistance."
        );
        // Optionally log the user out
        if (typeof logout === "function") logout();
    }
}